import React from 'react';
import { Link } from 'react-router-dom';
import './DataSciencePage.css';

const METHODS = [
  {
    icon: '📊',
    title: 'Frequency Distribution Analysis',
    tag: 'Descriptive Statistics',
    desc: 'Every lottery number is assigned a draw count across the full historical dataset. By plotting these counts as a bar chart ordered by number, we reveal the raw empirical distribution — a foundational step in any statistical exploration. Color encoding (hot/warm/neutral/cold) maps percentile rank to visual temperature, making outliers immediately apparent without needing to read axis values.',
    concepts: ['Empirical frequency', 'Percentile ranking', 'Data encoding', 'Exploratory Data Analysis (EDA)'],
  },
  {
    icon: '🗺',
    title: 'Heatmap Visualization',
    tag: 'Data Visualization',
    desc: 'The heatmap maps the same frequency data onto a spatial grid, using color intensity as the data channel. The color scale interpolates through indigo → purple → green, creating perceptually uniform transitions. This technique — common in genomics, financial analysis, and web analytics — lets viewers scan a two-dimensional structure and identify clusters of high or low activity instantly.',
    concepts: ['Sequential color scales', 'Perceptual uniformity', 'Grid-based visualization', 'Spatial encoding'],
  },
  {
    icon: '📈',
    title: 'Time Series & Trend Analysis',
    tag: 'Time Series Analysis',
    desc: 'Cumulative frequency curves are computed for individual numbers across 2,800+ chronologically ordered draws. A single-pass O(n·k) algorithm builds the running totals efficiently. The resulting multi-line chart reveals convergence behavior — numbers tend toward the mean frequency over time, a classic demonstration of the Law of Large Numbers. Users can inject arbitrary numbers to compare trajectories.',
    concepts: ['Cumulative time series', 'Law of Large Numbers', 'Algorithmic complexity', 'Multi-variate line charts'],
  },
  {
    icon: '🔔',
    title: 'Statistical Distribution Analysis',
    tag: 'Inferential Statistics',
    desc: 'The distribution tab answers a meta-question: how are the frequencies themselves distributed? A histogram of frequencies exposes whether the draw process produces a normal distribution (expected under true randomness) or a skewed one. Four statistics are computed: mean, median, standard deviation, and Fisher-Pearson skewness coefficient. Deviation from symmetry (|skewness| > 0.5) is flagged as noteworthy.',
    concepts: ['Histograms', 'Normal distribution', 'Standard deviation', 'Skewness coefficient', 'Summary statistics'],
  },
  {
    icon: '🔗',
    title: 'Co-occurrence & Pair Analysis',
    tag: 'Combinatorial Analysis',
    desc: 'Each 5-number draw contains C(5,2) = 10 unique pairs. Across 2,800+ draws, this yields ~28,000 pair observations. A hash map accumulates co-occurrence counts in O(n) time. The result is a frequency table of number pairs — a form of association analysis. Users can query any number to retrieve its top co-occurring partners, a technique directly analogous to market basket analysis in retail data science.',
    concepts: ['Combinatorics', 'Association analysis', 'Hash map aggregation', 'Market basket analogy'],
  },
  {
    icon: '⏱',
    title: 'Gap Analysis (Recency Weighting)',
    tag: 'Temporal Analysis',
    desc: 'Gap analysis tracks the number of draws since each ball last appeared — its "gap" or recency score. Numbers are sorted by gap descending to surface the most overdue. This is a form of recency weighting, a technique used in recommendation systems (RFM analysis), inventory management, and time-decay models. The visual gap bar provides an immediate proportional comparison.',
    concepts: ['Recency scoring', 'RFM analysis', 'Time-decay weighting', 'Sorted ranking'],
  },
  {
    icon: '🎯',
    title: 'Probabilistic Number Generation',
    tag: 'Probabilistic Modeling',
    desc: 'The number picker implements three sampling strategies: (1) weighted sampling where each number appears in the pool proportionally to its historical frequency — a basic Monte Carlo approach; (2) overdue-biased sampling from the top 40% most-gapped numbers; (3) uniform random sampling as a baseline. These represent the spectrum from data-informed to purely stochastic selection.',
    concepts: ['Monte Carlo sampling', 'Weighted random selection', 'Uniform distribution', 'Sampling strategies'],
  },
  {
    icon: '📋',
    title: 'Temporal Context — Draw History',
    tag: 'Data Provenance',
    desc: 'Raw draw data is presented in reverse-chronological order with frequency-aware ball coloring, grounding all analysis in the actual source records. Each ball is colored by its overall temperature — a cross-reference between historical context and aggregate statistics. This data provenance view is essential in any analytical workflow: always trace aggregate conclusions back to the raw data.',
    concepts: ['Data provenance', 'Temporal ordering', 'Cross-referencing', 'Contextual encoding'],
  },
];

const STACK = [
  { name: 'React 19', role: 'UI framework — component architecture, hooks, memoization', color: '#61dafb' },
  { name: 'Chart.js 4', role: 'Canvas-based charting — bar, line, histogram renders', color: '#ff6384' },
  { name: 'NY.gov Open Data API', role: 'Live data source — 5,000 draws per game via SODA REST API', color: '#4ade80' },
  { name: 'useMemo / useCallback', role: 'Derived state memoization — prevents redundant O(n) recomputation', color: '#a78bfa' },
  { name: 'RevenueCat', role: 'Subscription management — entitlement gating for pro features', color: '#facc15' },
  { name: 'React Router v6', role: 'Client-side routing — SPA navigation without page reloads', color: '#fb923c' },
];

export default function DataSciencePage() {
  return (
    <div className="ds-page">

      {/* NAV */}
      <nav className="ds-nav">
        <Link to="/" className="ds-nav-logo">LottoPickle</Link>
        <div className="ds-nav-right">
          <Link to="/app" className="ds-nav-link">Open App</Link>
          <Link to="/" className="ds-nav-link">Home</Link>
        </div>
      </nav>

      {/* HERO */}
      <div className="ds-hero">
        <span className="ds-tag">Data Science</span>
        <h1 className="ds-hero-title">
          The analytics behind<br />
          <span className="ds-hero-gradient">LottoPickle</span>
        </h1>
        <p className="ds-hero-sub">
          LottoPickle is a data science platform built on top of a publicly available lottery dataset.
          Each view in the app applies a distinct analytical technique — from descriptive statistics and
          time series analysis to combinatorics and probabilistic modeling. This page documents the
          methodology, implementation decisions, and underlying concepts.
        </p>
        <div className="ds-hero-actions">
          <Link to="/app" className="ds-btn-primary">Explore the data</Link>
          <a href="mailto:info@lottopickle.com" className="ds-btn-secondary">Get in touch</a>
        </div>
      </div>

      {/* DATASET */}
      <div className="ds-dataset-section">
        <div className="ds-dataset-grid">
          <div className="ds-dataset-text">
            <span className="ds-tag">The Dataset</span>
            <h2>New York State Lottery — Open Data</h2>
            <p>All analysis is performed on historical Powerball and Mega Millions draw data sourced from the New York State government open data portal (data.ny.gov). The dataset is publicly available, machine-readable, and updated after each draw.</p>
            <p>Data is fetched via the SODA (Socrata Open Data API) REST interface, requesting up to 5,000 records ordered chronologically. Each record contains a draw date and space-delimited winning numbers. Parsing, filtering, and normalization happen client-side in the browser.</p>
            <div className="ds-dataset-stats">
              <div className="ds-ds-stat"><span className="ds-ds-num">~2,800</span><span className="ds-ds-label">Powerball draws</span></div>
              <div className="ds-ds-stat"><span className="ds-ds-num">~2,900</span><span className="ds-ds-label">Mega Millions draws</span></div>
              <div className="ds-ds-stat"><span className="ds-ds-num">0</span><span className="ds-ds-label">Cost to access</span></div>
            </div>
          </div>
          <div className="ds-pipeline">
            <h3 className="ds-pipeline-title">Data Pipeline</h3>
            {[
              { step: '01', label: 'Fetch', desc: 'SODA REST API → JSON response' },
              { step: '02', label: 'Parse', desc: 'Map rows → {date, main[], bonus}' },
              { step: '03', label: 'Filter', desc: 'Apply date range (1y / 3y / 5y / all)' },
              { step: '04', label: 'Compute', desc: 'freq[], lastSeen[], pairs via useMemo' },
              { step: '05', label: 'Render', desc: 'Chart.js + CSS visualizations' },
            ].map(s => (
              <div className="ds-pipe-step" key={s.step}>
                <span className="ds-pipe-num">{s.step}</span>
                <div>
                  <span className="ds-pipe-label">{s.label}</span>
                  <span className="ds-pipe-desc">{s.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* METHODS */}
      <div className="ds-methods-section">
        <span className="ds-tag">Analytical Methods</span>
        <h2 className="ds-section-title">Eight analytical views,<br /><em>eight different techniques</em></h2>
        <p className="ds-section-sub">Each tab in the app applies a distinct data science concept to the same underlying dataset, demonstrating how a single data source can be interrogated from multiple analytical angles.</p>

        <div className="ds-methods-grid">
          {METHODS.map((m) => (
            <div className="ds-method-card" key={m.title}>
              <div className="ds-method-header">
                <span className="ds-method-icon">{m.icon}</span>
                <span className="ds-method-tag">{m.tag}</span>
              </div>
              <h3 className="ds-method-title">{m.title}</h3>
              <p className="ds-method-desc">{m.desc}</p>
              <div className="ds-method-concepts">
                {m.concepts.map(c => (
                  <span className="ds-concept-pill" key={c}>{c}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TECH STACK */}
      <div className="ds-stack-section">
        <span className="ds-tag">Implementation</span>
        <h2 className="ds-section-title">Tech stack</h2>
        <p className="ds-section-sub">Built entirely client-side — no backend required. All computation runs in the browser using standard React patterns.</p>
        <div className="ds-stack-grid">
          {STACK.map(s => (
            <div className="ds-stack-card" key={s.name}>
              <div className="ds-stack-dot" style={{ background: s.color }}></div>
              <div>
                <div className="ds-stack-name">{s.name}</div>
                <div className="ds-stack-role">{s.role}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KEY DECISIONS */}
      <div className="ds-decisions-section">
        <span className="ds-tag">Engineering Notes</span>
        <h2 className="ds-section-title">Key design decisions</h2>
        <div className="ds-decisions-grid">
          {[
            {
              title: 'Client-side computation',
              body: 'All statistical computation happens in the browser via React useMemo hooks. For a dataset of ~5,000 rows and up to 70 numbers, this is well within browser capabilities and eliminates the need for a backend API — reducing infrastructure cost and latency to zero.',
            },
            {
              title: 'Memoization strategy',
              body: 'Derived data (freq, lastSeen, pairs, distribution) is computed once per data+filter change using useMemo with precise dependency arrays. This prevents O(n) recomputation on every render. The pairs computation — O(10n) — runs in a single pass rather than nested lookups.',
            },
            {
              title: 'Freemium gating',
              body: 'Free views (Frequency, Hot/Cold, Heatmap, History) are chosen to showcase the depth of data without requiring payment. Pro views (Trends, Distribution, Pairs, Gap Analysis, Picker) represent deeper analytical capability and personalized features, aligning paywall placement with perceived value.',
            },
            {
              title: 'Date range filtering',
              body: 'Filtering by 1, 3, or 5 years is implemented as a useMemo over the raw draws array — all downstream computations automatically recompute from the filtered slice. This architecture means adding new filter dimensions requires zero changes to the visualization components.',
            },
          ].map(d => (
            <div className="ds-decision-card" key={d.title}>
              <h3 className="ds-decision-title">{d.title}</h3>
              <p className="ds-decision-body">{d.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="ds-cta">
        <h2>See the analysis in action</h2>
        <p>All eight analytical views are live in the app — free to explore.</p>
        <Link to="/app" className="ds-btn-primary">Open LottoPickle</Link>
      </div>

      {/* FOOTER */}
      <footer className="ds-footer">
        <div className="ds-footer-links">
          <Link to="/">Home</Link>
          <Link to="/app">App</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:info@lottopickle.com">Contact</a>
        </div>
        <p className="ds-footer-disclaimer">For entertainment and educational purposes only. Not gambling advice.</p>
      </footer>
    </div>
  );
}
